/**
 * Handles the Tenant config
 *  TODO: This currently only handles tags
 */

import Vue from 'vue'

/**
 * Import Dependency
 */

/**
 * Import API
 */

/**
 * Declare Variable
 */
const state = {
  tenant: process.env.VUE_APP_TENANT,
  loading: false,

  /**
   * Configuration attributes
   *  { uuid: '', label: '', enabled: true, request: true, process: true, deleted_at: null }
   */
  tags: [],
  contractors: [],

  /**
   * Configuration attributes
   *  { uuid: '', label: '', enabled: true, deleted_at: null }
   */
  pids: [],

  /**
   * Key - value storage of workflow uuids
   *  { realisation: 'uuid' }
   */
  workflows: {},

  workflowspecs: {},

  selectOptions: {},
  selectOptionLabelsByValue: {},
}

const getters = {
  /****************************************************************************
   * Alle
   */
  getTenant: state => state.tenant,

  /**
   * Check whether a tenant is the current tenant
   */
  isCurrentTenant: state => ({ tenant }) => state.tenant === tenant,


  /****************************************************************************
   * Tag related getters
   */

  /**
   * All tags, disabled, deleted or not
   */
  getTags: state => state.tags,

  /**
   * The tags that can be selected
   */
  getEnabledTags: state => state.tags.filter(tag => !! tag.enabled && ! tag.deleted_at),
  getExistingTags: state => state.tags.filter(tag => ! tag.deleted_at),

  /**
   * Get a list of tag labels by uuid. Includes all tags
   */
  getLabelsByTagUuid: state => state.tags.reduce(( result, tag ) => {
    result[tag.uuid] = tag.label
    return result
  }, {}),

  /**
   * Get a list of record's tag labels by uuid.
   */
  getRecordTagLabels: state => ({ record }) => {
    return record.tags.map(uuid => state.tags.find(tag => uuid === tag.uuid)?.label)
  },

  /****************************************************************************
   * PID related getters
   */
  getPIDs: state => state.pids,

  getBaseUrl: state => state.baseUrl,
  /**
   * The pids that can be selected
   */
  getEnabledPids: state => state.pids.filter(pid => !! pid.enabled),

  /**
   * Get a list of pid labels by uuid. Includes all pids
  */
  getLabelsByPidUuid: state => state.pids.reduce(( result, pid ) => {
    result[pid.uuid] = pid.label
    return result
  }, {}),

  /****************************************************************************
   * Realisation contractors related getters
   */
  getContractors: state => state.contractors,

  /**
   *
   * @returns {Array}
   */
  getExistingContractors: state => state.contractors.filter(contractor => ! contractor.deleted_at),

  /**
   * The contractors that can be selected
   */
  getEnabledContractors: state => state.contractors.filter(contractor => !! contractor.enabled && ! contractor.deleted_at),

  /**
   * Get a list of contractor labels by uuid. Includes all contractors
   */
  getLabelsByContractorUuid: state => state.contractors.reduce(( result, contractor ) => {
    result[contractor.uuid] = contractor.label
    return result
  }, {}),

  /**
   * Get a specific contractor option by uuid
   * @returns {Object}
   */
  getContractorByUuid: state => ({ uuid }) => state.contractors.find(contractor => contractor.uuid === uuid),

  /****************************************************************************
   * Workflow processes
   */

  /**
   * Get the flow uuid by name (e.g. realisation)
   */
  getWorkflowUuidByName: state => ({ name }) => state.workflows[name] || null,

  getWorkflowSpecByUuid: state => ({ uuid }) => state.workflowspecs[uuid] || null,

  getWorkflowSpecByName: state => ({ name }) => {
    let uuid = state.workflows[name] || null
    return uuid ? (state.workflowspecs[uuid] || null) : null
  },

  getWorkflowStepSpecByUuid: state => ({ flowUuid, stepUuid }) => {
    return state.workflowspecs[flowUuid]?.stepsByUuid?.[stepUuid] || null
  },

  /****************************************************************************
   * Select options
   */
  getSelectOptionsByName: state => ({ name }) => {
    return state.selectOptions[name] || []
  },
  getSelectOptionLabelByValue: state => ({ name, value }) => {
    return (state.selectOptionLabelsByValue[name] || {})[value] || null
    // let option = (state.selectOptions[name] || []).find(option => option.value === value)
    // return option ? (option.text || 'onbekend') : 'onbekend'
  },


  /****************************************************************************
   * Process types
   */
  getProcessTypeOptions: (state, getters) => {
    return getters.getSelectOptionsByName({ name: 'processtypes' })
  },
  getProcessTypeOptionsByContext: (state, getters) => ({ context }) => {
    let options = getters.getSelectOptionsByName({ name: 'processtypes' })
    return (context === null)
      ? options
      : options.filter(option => option?.meta?.context === context)
  },


}
const actions = {
  /**
   * Load the tenant configuration
   */
  loadConfig: async ({ state, commit }, { token }) => {
    if (state.loading) return

    commit('setLoadingState', { loading: true })
    //console.log("USING OLD API")
    const api = await fetch('/api/config', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (! api.ok) {
      commit('setLoadingState', { loading: false })

      throw new Error('Failed to load config')

    } else {
      const response = await api.json()

      commit('setConfig', {
        config: response.data,
      })
      commit('setLoadingState', { loading: false })
    }
  },

  loadWorkflowSpecs: async function({ state, commit }) {

    let uuids = Object.values(state.workflows)

    for (let i = 0; i < uuids.length; i++) {

      const response = await this.$cbapi.config.getProcessSpecificationByUuid({
        uuid: uuids[i],
      })

      // const api = await fetch('/api/workflowspecification', {
      //   method: 'POST',
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //   },
      //   body: JSON.stringify({
      //     uuid: uuids[i],
      //   }),
      // })

      // if (! api.ok) {
      //   throw new Error('Failed to load config')
      // }

      // const response = await api.json()
      if (! response) {
        throw new Error('Failed to load config')
      }
      // TODO: CBAPI - DEFERRED - Create Model? Change the API output ?
      const spec = response.data
      spec.stepsByUuid = {}
      spec.finalStepUuid = null

      // Process the data to make it more easily accessible
      response.data.steps.forEach(step => {
        spec.stepsByUuid[step.uuid] = step
        if (step.final) {
          spec.finalStepUuid = step.uuid
        }
      })

      commit('setWorkflowSpecByUuid', {
        uuid: uuids[i],
        spec,
      })
    }
  },
}
const mutations = {
  setLoadingState(state, { loading }) {
    state.loading = loading
  },
  /**
   * Set the configuration in the store
   */
  setConfig(state, { config }) {
    if (config.tenant !== state.tenant) {
      throw new Error('Wrong tenant config')
    }

    // console.log(config)

    state.tags = config.tags || []
    state.pids = (config.pids || []).sort((a, b) => a.label.localeCompare(b.label))
    state.contractors = (config.contractors || []).sort((a, b) => a.label.localeCompare(b.label))
    // state.workflows = config.workflows || {}
    // TODO: CBAPI - TEMP HARDCODED !
    state.workflows = {
      realisation: '53c35fb3-c0f5-439c-8924-f0e4d7bf9e47',
    }
    state.selectOptions = config.selectOptions || {}
    state.baseUrl = config.baseUrl


    /**
     * At various occasions we need to obtain an option label by an option value.
     *  To avoid having to run .find on every lookup, this cache is created.
     */
    state.selectOptionLabelsByValue = Object.keys(state.selectOptions)
      .reduce((result, key) => {
        result[key] = state.selectOptions[key].reduce((optionLabelsByValue, option) => {
          optionLabelsByValue[option.value] = option.text || ''
          return optionLabelsByValue
        }, {})
        return result
      }, {})
  },

  setWorkflowSpecByUuid(state, { uuid, spec }) {
    Vue.set(state.workflowspecs, uuid, spec)
  },
}

/**
 * Export
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
